import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Save,
  HelpCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_blogfaq,
  delete_website_master_data,
  get_all_blogfaq,
} from "../ServiceConnection/serviceconnection.js";

// ─── Constants ────────────────────────────────────────────────────────────────
// Backend model AdminBlogfaq:
// primary_id, entry_date, question_name, answer_name, page_name, flag

const ITEMS_PER_PAGE = 8;

const EMPTY_FORM = {
  question_name: "",
  answer_name: "",
  page_name: "", // ← fix: was missing, was incorrectly bound to answer_name
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
const DeleteModal = ({ faq, onConfirm, onClose, deleteLoader }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div
      className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    />
    <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card shadow-2xl">
      <div className="p-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
          <Trash2 className="h-6 w-6 text-destructive" />
        </div>
        <h3 className="mb-2 text-base font-bold text-foreground">
          Delete FAQ?
        </h3>
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-foreground">
            "{faq?.question_name}"
          </span>
          ? This cannot be undone.
        </p>
      </div>
      <div className="flex gap-3 border-t border-border p-4">
        <Button
          variant="outline"
          className="flex-1 rounded-xl"
          onClick={onClose}
          disabled={deleteLoader}
        >
          No
        </Button>
        <Button
          disabled={deleteLoader}
          className="flex-1 gap-2 rounded-xl bg-destructive text-white hover:bg-destructive/90"
          onClick={onConfirm}
        >
          {deleteLoader ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Yes, Delete
        </Button>
      </div>
    </div>
  </div>
);

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────
const FAQModal = ({ faq, onSave, onClose, saveLoader }) => {
  const [form, setForm] = useState(
    faq
      ? {
          question_name: faq.question_name || "",
          answer_name: faq.answer_name || "",
          page_name: faq.page_name || "", // ← fix: properly initialized
        }
      : EMPTY_FORM,
  );
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.question_name.trim()) e.question_name = "Question is required";
    if (!form.answer_name.trim()) e.answer_name = "Answer is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form);
  };

  const handleChange = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-border bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border p-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
              <HelpCircle className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">
                {faq ? "Edit FAQ" : "Add FAQ"}
              </h2>
              <p className="text-xs text-muted-foreground">
                {faq
                  ? "Update the question and answer"
                  : "Add a new question to your website"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 transition-colors hover:bg-accent"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 p-6">
          {/* question_name */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Question <span className="text-destructive">*</span>
            </label>
            <Input
              id="question_name"
              name="question_name"
              placeholder="Enter question..."
              value={form.question_name}
              onChange={(e) => handleChange("question_name", e.target.value)}
              className={errors.question_name ? "border-destructive" : ""}
            />
            {errors.question_name && (
              <p className="mt-1 text-xs text-destructive">
                {errors.question_name}
              </p>
            )}
          </div>

          {/* answer_name — FormData param: answer_name_dd */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Answer <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="answer_name_dd"
              name="answer_name_dd"
              placeholder="Enter answer..."
              rows={5}
              minLength={3}
              maxLength={6000}
              value={form.answer_name}
              onChange={(e) => handleChange("answer_name", e.target.value)}
              className={errors.answer_name ? "border-destructive" : ""}
            />
            {errors.answer_name && (
              <p className="mt-1 text-xs text-destructive">
                {errors.answer_name}
              </p>
            )}
            <p className="mt-1 text-[11px] text-muted-foreground">
              {form.answer_name.length} / 6000 characters
            </p>
          </div>

          {/* page_name — ← fix: own state field, own onChange */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Page Name
              <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                (optional — e.g. home, contact)
              </span>
            </label>
            <Input
              id="page_name"
              name="page_name"
              placeholder="e.g. home"
              value={form.page_name}
              onChange={(e) => handleChange("page_name", e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 border-t border-border p-4">
          <Button
            disabled={saveLoader}
            onClick={handleSave}
            className="flex-1 gap-2 rounded-xl"
          >
            {saveLoader ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {faq ? "Save Changes" : "Save"}
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saveLoader}
            className="rounded-xl"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── FAQ Table Row ────────────────────────────────────────────────────────────
const FAQTableRow = ({ faq, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <tr className="border-b border-border transition-colors hover:bg-muted/30">
      {/* Create Date */}
      <td className="px-6 py-4">
        <p className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDate(faq.entry_date)}
        </p>
      </td>

      {/* Question */}
      <td className="max-w-[220px] px-6 py-4">
        <p className="text-sm font-semibold text-foreground">
          {faq.question_name}
        </p>
        {faq.page_name && (
          <span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
            {faq.page_name}
          </span>
        )}
      </td>

      {/* Answer */}
      <td className="hidden max-w-[300px] px-6 py-4 md:table-cell">
        <p
          className={`text-xs leading-relaxed text-muted-foreground ${
            expanded ? "" : "line-clamp-2"
          }`}
        >
          {faq.answer_name}
        </p>
        {(faq.answer_name?.length || 0) > 100 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1 text-[11px] font-medium text-primary hover:underline"
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </td>

      {/* Actions */}
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            title="Edit"
            onClick={() => onEdit(faq)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            title="Delete"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => onDelete(faq)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const FAQManager = () => {
  const { toast } = useToast();

  const [faqs, setFaqs] = useState([]);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editorDataMainID, setEditorDataMainID] = useState("0");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // ─── Fetch ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setFetchLoader(true);

      const fd = new FormData();
      fd.append("flag", "1");

      const response = await server_post_data(get_all_blogfaq, fd);

      if (!response.data.error) {
        setFaqs(response.data.message.data || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFetchLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Save / Update ────────────────────────────────────────────────────────
  const handleSaveChangesdynamic = async (form) => {
    try {
      setSaveLoader(true);

      const fd = new FormData();
      fd.append("main_id", editorDataMainID); // 0=create, >0=update
      fd.append("question_name", form.question_name);
      fd.append("answer_name_dd", form.answer_name); // exact backend param name
      fd.append("page_name", form.page_name || "");

      const response = await server_post_data(save_update_blogfaq, fd);

      if (!response.data.error) {
        toast({
          title: "Success",
          description: response.data.message,
        });
        handleCloseModal();
        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaveLoader(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoader(true);

      const fd = new FormData();
      fd.append("id_for_delete", deleteTarget.primary_id);
      fd.append("flag_for", "8"); // AdminBlogfaq in FLAG_MODEL_MAP
      fd.append("for_status_final", "d");

      const response = await server_post_data(delete_website_master_data, fd);

      if (!response.data.error) {
        toast({
          title: "Deleted",
          description: response.data.message,
        });
        setDeleteTarget(null);
        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteLoader(false);
    }
  };

  // ─── Modal handlers ───────────────────────────────────────────────────────
  const handleAdd = () => {
    setEditingFaq(null);
    setEditorDataMainID("0");
    setModalOpen(true);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setEditorDataMainID(faq.primary_id);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingFaq(null);
    setEditorDataMainID("0");
  };

  // ─── Search + Pagination ──────────────────────────────────────────────────
  const filtered = faqs.filter((f) =>
    [f.question_name, f.answer_name, f.page_name]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">FAQ Manager</h2>
            <p className="text-sm text-muted-foreground">
              Manage the FAQs shown on your website pages.
            </p>
          </div>
          <Button className="gap-2 self-start rounded-xl" onClick={handleAdd}>
            <Plus className="h-4 w-4" /> Add FAQ
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search questions..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9"
          />
        </div>

        {/* Table */}
        <Card>
          {/* Loading */}
          {fetchLoader && (
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          )}

          {/* Empty */}
          {!fetchLoader && paginated.length === 0 && (
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                <HelpCircle className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                No FAQs found
              </p>
              <p className="mb-4 text-xs text-muted-foreground">
                {search
                  ? "Try a different search term."
                  : "Add your first FAQ to get started."}
              </p>
              {!search && (
                <Button
                  size="sm"
                  className="gap-2 rounded-xl"
                  onClick={handleAdd}
                >
                  <Plus className="h-4 w-4" /> Add FAQ
                </Button>
              )}
            </CardContent>
          )}

          {/* Rows */}
          {!fetchLoader && paginated.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Create Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Question
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground md:table-cell">
                        Answer
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((faq) => (
                      <FAQTableRow
                        key={faq.primary_id}
                        faq={faq}
                        onEdit={handleEdit}
                        onDelete={setDeleteTarget}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t border-border px-6 py-4">
                <p className="text-xs text-muted-foreground">
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                  {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of{" "}
                  {filtered.length} FAQs
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === totalPages ||
                        Math.abs(p - currentPage) <= 1,
                    )
                    .map((p, i, arr) => (
                      <span key={p} className="flex items-center gap-1">
                        {i > 0 && arr[i - 1] !== p - 1 && (
                          <span className="px-1 text-xs text-muted-foreground">
                            …
                          </span>
                        )}
                        <button
                          onClick={() => setCurrentPage(p)}
                          className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-medium transition-all ${
                            currentPage === p
                              ? "bg-gradient-primary text-white shadow-glow"
                              : "border border-border text-muted-foreground hover:border-primary/30 hover:text-primary"
                          }`}
                        >
                          {p}
                        </button>
                      </span>
                    ))}
                  <Button
                    variant="outline"
                    size="icon-sm"
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Footer count */}
        {!fetchLoader && (
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {faqs.length} FAQs
          </p>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modalOpen && (
        <FAQModal
          faq={editingFaq}
          onSave={handleSaveChangesdynamic}
          onClose={handleCloseModal}
          saveLoader={saveLoader}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <DeleteModal
          faq={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          deleteLoader={deleteLoader}
        />
      )}
    </>
  );
};

export default FAQManager;
